import { animate, state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, inject, signal, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { AccountType, OTPChannelType } from 'src/app/shared/model/auth-channels';
import { IntegratorCreation } from 'src/app/shared/model/integrator.model';
import { MerchantCreation } from 'src/app/shared/model/merchant-details.model';
import { AuthService } from 'src/app/shared/services/auth.service';
import { LogToastService } from 'src/app/shared/services/log-toast/log-toast.service';
import { CarouselComponent } from '../../components/carousel/carousel.component';
import { OtpComponent, OtpLoad } from '../../components/otp/otp.component';
import { STEPPER_IMPORTS, StepperComponent } from '../../components/stepper/stepper.component';
import { AppRoutes } from '../../routes/common.routes';
import { AuthStateService } from '../../services/auth/auth-state.service';
import { WebConfigService } from '../../services/web-config/web-config.service';
import { ContactStepComponent } from './contact-step/contact-step.component';
import { ContactStepSubmission } from './contact-step/contact-step.model';
import { DetailsStepComponent } from './details-step/details-step.component';
import { DetailsStepFormModel } from './details-step/details-step.model';
import { RegisterPasswordStepComponent } from './register-password-step/register-password-step.component';
import { RegisterPinStepComponent } from './register-pin-step/register-pin-step.component';
import { RegistrationService } from './register.service';

@Component({
  selector: 'app-merchant-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
  animations: [
    trigger('flyInOut', [
      state('in', style({ transform: 'translateX(0)' })),
      transition('void => *', [style({ transform: 'translateX(100%)' }), animate('300ms ease-out')]),
      transition('* => void', [animate('300ms ease-out', style({ transform: 'translateX(-100%)' }))]),
    ]),
  ],
  imports: [
    STEPPER_IMPORTS,
    CarouselComponent,
    OtpComponent,
    DetailsStepComponent,
    ContactStepComponent,
    RegisterPinStepComponent,
    RegisterPasswordStepComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class RegisterComponent {
  #authState = inject(AuthStateService);
  #registrationService = inject(RegistrationService);
  private readonly stepper = viewChild.required(StepperComponent);
  #logToast = inject(LogToastService);
  private webconfig = inject(WebConfigService);

  public authService = inject(AuthService);
  public router = inject(Router);

  public orgId: string = this.webconfig.orgId;
  public logo = this.webconfig.assets.logo;
  public logoDark = this.webconfig.assets.logoWhite;
  public termsAndConditionsUrl = signal(this.webconfig.assets.termsAndConditionsUrl);
  public apkPackageName = this.webconfig.apkPackageName;
  public loginType: string = this.webconfig.loginType;
  public accountType: string = this.webconfig.accountType;
  public portal = this.webconfig.portal;
  public backgroundImage = this.webconfig.assets.authScreenBackground;

  public channelType: OTPChannelType = OTPChannelType.Email;
  public channelTypeValue = signal<string | undefined>(undefined);
  public verifyGUID = signal('');

  public showContactStep = signal(false);
  public detailsInfo = signal<DetailsStepFormModel>({ firstName: '', lastName: '', entityName: '', country: '' });
  public contactInfo = signal<ContactStepSubmission>({ username: '', email: '', mobileNumber: undefined });

  constructor() {
    this.#registrationService
      .getTermsAndConditions(this.apkPackageName)
      .then((response) => this.termsAndConditionsUrl.set(response));
    this.authService.removeLoginCredentials();
  }

  public goBack(): void {
    this.stepper().previous();
  }

  public goBackToDetails(): void {
    this.showContactStep.set(false);
  }

  public onDetailsSubmitted(details: DetailsStepFormModel): void {
    this.detailsInfo.set(details);
    this.showContactStep.set(true);
  }

  public onContactSubmitted(contact: ContactStepSubmission): void {
    this.contactInfo.set(contact);
    this.channelTypeValue.set(contact.username);
    this.stepper().next();
  }

  public onVerified(otpLoad: OtpLoad): void {
    this.verifyGUID.set(otpLoad.id);
    this.stepper().next();
  }

  public async onCredentialSubmit(password: string): Promise<void> {
    let observable: Observable<unknown>;

    try {
      if (this.accountType === AccountType.Merchant) {
        observable = this.#registrationService.createMerchant(this.#buildMerchantAccount(password));
      } else if (this.accountType === AccountType.Integrator) {
        observable = this.#registrationService.createIntegrator(this.#buildIntegratorAccount(password));
      } else {
        this.#logToast.error('MESSAGES.ERRORS.INVALID_ACCOUNT_TYPE', undefined, undefined, {
          logger: true,
          toast: false,
        });
        return;
      }
      observable.subscribe({
        next: () => this.loginAfterRegistration(password),
        error: (error) => {
          this.#logToast.error('MESSAGES.ERRORS.ERROR_CREATING_ACCOUNT_TYPE', error, { accountType: this.accountType });
        },
      });
    } catch (error) {
      this.#logToast.error('MESSAGES.ERRORS.ERROR_CREATING_ACCOUNT_TYPE', error, { accountType: this.accountType });
    }
  }

  public async loginAfterRegistration(password: string): Promise<void> {
    const { username } = this.contactInfo();
    await this.authService.login(username, password);

    const user = this.#authState.user();
    if (user) {
      this.router.navigate([AppRoutes.dashboard]);
    } else {
      this.router.navigate(['/']);
    }
  }

  #buildMerchantAccount(password: string): MerchantCreation {
    const { username, email, mobileNumber } = this.contactInfo();
    const { entityName, firstName, lastName } = this.detailsInfo();

    const merchant: MerchantCreation = {
      username,
      entityName,
      contactName: `${firstName} ${lastName}`,
      contactEmail: email,
      password,
      verifyGUID: this.verifyGUID(),
      verificationChannel: this.channelType,
      acquirerDetails: {
        apkPackageName: this.apkPackageName,
      },
    };

    if (this.channelType == OTPChannelType.SMS) {
      merchant['contactNumber'] = mobileNumber;
    }

    return merchant;
  }

  #buildIntegratorAccount(password: string): IntegratorCreation {
    const { username, email, mobileNumber } = this.contactInfo();
    const { entityName, firstName, lastName, country } = this.detailsInfo();

    const integrator: IntegratorCreation = {
      username,
      entityName,
      contactName: `${firstName} ${lastName}`,
      contactEmail: email,
      password,
      verifyGUID: this.verifyGUID(),
      verificationChannel: this.channelType,
      appName: this.apkPackageName,
      region: country,
    };

    if (this.channelType == OTPChannelType.SMS) {
      integrator['contactNumber'] = mobileNumber;
    }

    return integrator;
  }
}
